import userflow from 'userflow.js';
import ReactGA from 'react-ga4';
import groundcover from '@groundcover/browser';
import Userback from '@userback/widget';
import { logIfDebug } from '@/lib/frontendDebug';
import { UserModel } from '@/apiTypes/UserTypes';
import TagManager from 'react-gtm-module';

/** True after the first successful `groundcover.init` this page load. Further `initThirdParty` runs use `updateConfig` only so we do not replace the SDK or call `startReplayRecording` again. */
let groundcoverSdkInitialized = false;
/** True after `userflow.init` has been called this page load. Userflow inits lazily inside `identifyUserflowUser` so unauthenticated visitors never load the SDK and never count toward MAU. */
let userflowInitialized = false;

/**
 * RUM network events carry each API call's request and response bodies (up to 5,000 characters):
 * chat messages, Support submissions, assessment answers and results. Clear them before the event
 * is queued; URL, method, status, timing and errors stay. asa's server-side traces keep the payloads.
 * By the time the SDK calls enrichEvent it has flattened the attributes, so the bodies are the keys
 * `gc.request.body` and `gc.response.body` (its NetworkEventAttributes type shows them nested).
 */
export function stripNetworkBodies<T extends { type: string; attributes?: unknown }>(event: T): T {
    if (event.type === 'network' && event.attributes && typeof event.attributes === 'object') {
        const attributes = event.attributes as Record<string, unknown> & { gc?: { request?: { body?: string }; response?: { body?: string } } };
        if ('gc.request.body' in attributes) attributes['gc.request.body'] = '';
        if ('gc.response.body' in attributes) attributes['gc.response.body'] = '';
        if (attributes.gc?.request) attributes.gc.request.body = '';
        if (attributes.gc?.response) attributes.gc.response.body = '';
    }
    return event;
}

const EMAIL_ADDRESS = /[A-Za-z0-9._%+-]+(?:@|%40)[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/g;
const SECRET_QUERY_VALUES = /([?&#](?:nonce|token|session_token|access_token|id_token|refresh_token|email)=)[^&#\s"]*/gi;
const CALLBACK_QUERY_VALUES = /([?&#](?:code|state)=)[^&#\s"]*/gi;
const CALLBACK_PATH = /\/(?:callback|account-linking\/session|link-accounts|login-error)(?:[?#]|$)/;

/**
 * Redacts sign-in values and email addresses from one string. Tokens, nonce and email query values go
 * on every URL; code and state only on the sign-in callback pages, because `/?code=success` (email
 * verification) is worth keeping for debugging. Same scope as the server-side log and trace rules.
 */
export function redactSensitiveValues(value: string): string {
    let result = value.replace(SECRET_QUERY_VALUES, '$1REDACTED');
    if (CALLBACK_PATH.test(result)) result = result.replace(CALLBACK_QUERY_VALUES, '$1REDACTED');
    return result.includes('@') || result.includes('%40') ? result.replace(EMAIL_ADDRESS, 'REDACTED') : result;
}

function redactStrings(target: Record<string, unknown>): void {
    for (const key of Object.keys(target)) {
        const value = target[key];
        if (typeof value === 'string') target[key] = redactSensitiveValues(value);
        else if (value && typeof value === 'object') redactStrings(value as Record<string, unknown>);
    }
}

/**
 * RUM events go from the browser straight to groundcover, so the cluster's redaction never sees them.
 * Every event carries the page address (`location.url`), and page loads, navigation and network events
 * carry more URLs (`page_url`, `page_referrer`, `from_url`, `url.full`), query strings included: the
 * account-linking callback's code and state, and Auth0's error page. Redact every string attribute
 * except the replay payload, which `rr-block` masks.
 */
export function redactUrlValues<T extends { type: string; attributes?: unknown }>(event: T): T {
    if (event.type !== 'replay' && event.attributes && typeof event.attributes === 'object') {
        redactStrings(event.attributes as Record<string, unknown>);
    }
    return event;
}

/** The SDK's single `enrichEvent` hook: runs on every event before it is queued. */
export function redactRumEvent<T extends { type: string; attributes?: unknown }>(event: T): T {
    return redactUrlValues(stripNetworkBodies(event));
}

/**
 * Requests to asa's own API carry a W3C `traceparent`, so a RUM network event and the backend trace
 * are one trace (asa already continues it to kuder-ai and KAE). The SDK matches each pattern as an
 * anchored regex against the path of a relative URL and the whole of an absolute one; RTK Query sends
 * absolute URLs, raw fetches relative ones. Same-origin only, so no third party receives the header.
 */
export function apiTracePropagationUrls(): string[] {
    const origin = typeof window === 'undefined' ? '' : window.location.origin;
    return origin ? ['/api/*', `${origin}/api/*`] : ['/api/*'];
}

/** Initialize or update Groundcover from public config. Safe to call during render when configs are known (see `LoadThirdParty`). */
export function syncGroundcoverPublicConfig(configs: { [key: string]: string }): void {
    const groundcoverApiKey = configs['groundcoverApiKey'];
    if (!groundcoverApiKey) {
        return;
    }
    const sessionReplayEnabled = configs['groundcoverSessionReplay'] !== 'false';
    const params = {
        apiKey: groundcoverApiKey,
        cluster: configs['groundcoverCluster'],
        environment: configs['groundcoverEnvironment'],
        dsn: configs['groundcoverDsn'],
        appId: configs['groundcoverAppId'],
        // Click and keypress events otherwise carry the clicked element's text and every key typed.
        // With masking on, click text is sent as "*", and so are keys typed into fields marked
        // `data-private` (the shared Input and Textarea).
        options: { enableMasking: true, enrichEvent: redactRumEvent, tracePropagationUrls: apiTracePropagationUrls() },
    };
    if (!groundcoverSdkInitialized) {
        groundcover.init(params);
        groundcoverSdkInitialized = true;
        if (sessionReplayEnabled && typeof groundcover.startReplayRecording === 'function') {
            groundcover.startReplayRecording();
            logIfDebug(configs, '[Groundcover] session replay started (init)');
        }
    }
    else {
        groundcover.updateConfig(params);
        logIfDebug(configs, '[Groundcover] public config synced (SDK already initialized)');
    }
}

export const initThirdParty = async (configs: { [key: string]: string }): Promise<void> => {
    // Groundcover must init synchronously before any `await`. Otherwise `identifyUser` in AppRoutes
    // can run before the SDK exists (Anonymous in RUM). Previously AppRoutes called `init()` there,
    // which masked that race.
    syncGroundcoverPublicConfig(configs);

    const googleTagManagerId = configs['googleTagManagerId'];
    if (googleTagManagerId) {
        TagManager.initialize({ gtmId: googleTagManagerId });
    }

    const ga4tag = configs['ga4tag'];
    if (ga4tag) {
        ReactGA.initialize(ga4tag);
    }

    const userBackApiKey = configs['userbackApiKey'];
    if (userBackApiKey) {
        try {
            await Userback(userBackApiKey);
        }
        catch (e) {
            console.error('Error initializing userback', e);
        }
    }
};

export const fetchPublicConfigs = async (): Promise<{ [key: string]: string }> => {
    try {
        const response = await fetch('/api/config/public');

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to fetch config: ${response.status} - ${errorText}`);
        }

        const configs: { [key: string]: string } = await response.json();

        if (Object.keys(configs).length === 0) {
            throw new Error('Public configs not found.');
        }

        return configs;
    }
    catch (error) {
        console.error('Error getting frontendConfigs', error);
        return {};
    }
};

/**
 * RUM user identity. Safe after `syncGroundcoverPublicConfig` / `groundcover.init`.
 * Re-runs from `GroundcoverIdentifyEffect` when RTK user (uid/sub) updates.
 */
export function identifyGroundcoverUserInBrowser(
    configs: { [key: string]: string },
    user: { uid?: string; sub?: string } | null | undefined,
): void {
    const groundcoverApiKey = configs['groundcoverApiKey'];
    if (!groundcoverApiKey || !user?.uid) {
        return;
    }
    // Only the Britebound UID: a one-way UUID v5 of the Auth0 user id, so the sub can't be recovered
    // from it. The sub itself is left out because for social logins it embeds the provider's account
    // id (e.g. a Google account number). `email` is not a mailbox: groundcover's session list labels
    // sessions by it (empty shows "Anonymous"), so it carries the same UID.
    groundcover.identifyUser({ id: user.uid, email: user.uid });
}

/** Identify the authenticated user in Userflow. Call this when `GET /api/user` resolves. Lazily inits the SDK on first call so unauthenticated visitors never load Userflow (keeps anonymous MAU at zero). */
export function identifyUserflowUser(configs: { [key: string]: string }, user: UserModel): void {
    const userflowToken = configs['userflow'];
    if (!userflowToken || !user.id) return;
    if (!userflowInitialized) {
        userflow.init(userflowToken);
        userflowInitialized = true;
    }
    userflow.identify(user.id, {}, { signature: user.userProfile?.userIdUserflowSignature ?? '' });
    logIfDebug(configs, '[Userflow] identifyUser');
}
